import React, { useEffect, useRef, type HTMLAttributes } from 'react';
import cx from 'classnames';
import useId from '@mc/hooks/useId';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import Text from '../Text';
import ButtonOrLink from '../ButtonOrLink';

import stylesheet from './ActionList.css';

export type ActionListItemProps = {
  /** `children` will not be supported in the long term.
   * Default to using the `label` prop. */
  children?: React.ReactNode;
  /** Secondary text for each action item */
  description?: string;
  /** Actions that are redirected as links */
  href?: string;
  /** Icon in front of the label */
  icon?: React.ReactNode;
  /** Icon at the end of the label */
  iconEnd?: React.ReactNode;
  /** Title of each action item */
  label?: string;
  /** Mouse click event */
  onClick?:
    | (() => void)
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void);
} & HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>;

const ActionListItem = ({
  children,
  label,
  description,
  icon,
  iconEnd,
  onClick,
  href,
  ...rest
}: ActionListItemProps) => {
  const describeId = useId();
  const isOrganismsPart2On = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );
  const isBugFixDFlagOn = mcdsFlagCheck('xp_mcds_redesign_bug_fixes_D');

  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isBugFixDFlagOn) {
      return;
    }
    // Add .custom-focus-state class when keypress on MenuItem is detected
    const handleKeyDown = () => {
      // eslint-disable-next-line no-unused-expressions
      itemRef.current?.classList.add(stylesheet['custom-focus-state']);
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isBugFixDFlagOn]);

  return (
    <div
      className={cx(
        stylesheet.item,
        (rest as { disabled?: boolean }).disabled && stylesheet.disabled,
      )}
      ref={isBugFixDFlagOn ? itemRef : undefined}
    >
      <ButtonOrLink
        onClick={onClick}
        className={stylesheet.label}
        href={href}
        aria-describedby={description ? describeId : undefined}
        {...rest}
      >
        {icon} <div>{label ? label : children}</div> {iconEnd}
      </ButtonOrLink>
      {description && (
        <Text
          appearance={isOrganismsPart2On ? 'tiny' : 'medium-secondary'}
          id={describeId}
          className={stylesheet.description}
        >
          {description}
        </Text>
      )}
    </div>
  );
};

export default ActionListItem;
